import { useCallback, useRef, useState } from 'react'

export interface DragOffset {
  x: number
  y: number
}

/**
 * Lets a panel be dragged by a handle.
 *
 * Built by hand rather than pulled in as a dependency: this is a pointer listener and two
 * numbers, and the behaviour that matters is all in the details below.
 *
 * - **Pointer events, not mouse events**, so a trackpad, a pen and a touchscreen all work
 *   from the same code path.
 * - **Pointer capture**, so dragging does not stop the moment the pointer leaves the handle.
 *   Without it a quick drag "sticks" as soon as the cursor outruns the title bar.
 * - **An offset, not a position.** The dialog stays centred by CSS and this translates it,
 *   so it is still centred when the viewport is resized and needs no measuring on open.
 * - **Clamped to the viewport on release**, so a dialog can never be dragged somewhere it
 *   cannot be dragged back from — which, with a modal overlay, means a page nobody can use.
 * - **Reset by the owner when it closes**, so reopening a dialog does not reopen it
 *   somewhere unexpected.
 * - Text selection is suppressed only while dragging, so selecting a title is still possible.
 */
export function useDraggable() {
  const [offset, setOffset] = useState<DragOffset>({ x: 0, y: 0 })
  const panelRef = useRef<HTMLDivElement | null>(null)
  const origin = useRef<{ pointerX: number; pointerY: number; x: number; y: number } | null>(null)
  const [dragging, setDragging] = useState(false)

  /**
   * Puts the dialog back in the middle.
   *
   * <p>Called by the owner when the dialog closes rather than run from an effect watching
   * `open`. Resetting in an effect meant a state write on every close, which is the
   * cascading-render pattern the lint rule exists to catch — and the owner already has an
   * `onOpenChange` callback, so there is nothing to watch for.
   */
  const reset = useCallback(() => {
    setOffset({ x: 0, y: 0 })
    setDragging(false)
    origin.current = null
  }, [])

  const onPointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      // Left button or a touch only. A right-click or a middle-click drag is not a move,
      // and reacting to them steals the context menu.
      if (event.button !== 0) return
      // Never start a drag from something interactive inside the handle — the close button
      // lives there, and a drag that swallows its click makes the dialog feel broken.
      if ((event.target as HTMLElement).closest('button, a, input, select, textarea')) return

      origin.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        x: offset.x,
        y: offset.y,
      }
      event.currentTarget.setPointerCapture(event.pointerId)
      setDragging(true)
    },
    [offset.x, offset.y],
  )

  const onPointerMove = useCallback((event: React.PointerEvent<HTMLElement>) => {
    const start = origin.current
    if (!start) return
    setOffset({
      x: start.x + (event.clientX - start.pointerX),
      y: start.y + (event.clientY - start.pointerY),
    })
  }, [])

  const onPointerUp = useCallback((event: React.PointerEvent<HTMLElement>) => {
    if (!origin.current) return
    origin.current = null
    setDragging(false)
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }

    // Pull it back if it has been dragged mostly off screen. A margin rather than zero, so
    // a little overhang is allowed but the title bar always stays grabbable.
    const panel = panelRef.current
    if (!panel) return
    const box = panel.getBoundingClientRect()
    const margin = 48
    setOffset((current) => {
      let { x, y } = current
      if (box.right < margin) x += margin - box.right
      if (box.left > window.innerWidth - margin) x -= box.left - (window.innerWidth - margin)
      if (box.bottom < margin) y += margin - box.bottom
      if (box.top > window.innerHeight - margin) y -= box.top - (window.innerHeight - margin)
      return { x, y }
    })
  }, [])

  return {
    panelRef,
    offset,
    dragging,
    reset,
    /** Spread onto the element that should act as the title bar. */
    handleProps: { onPointerDown, onPointerMove, onPointerUp, onPointerCancel: onPointerUp },
  }
}
